/**
 * Startinhalt für Impressum/Datenschutzerklärung (Plattform › Impressum &
 * Datenschutz, `PlatformLegalPage.tsx`) — reines HTML, wie es der
 * Tiptap-Editor im Frontend erzeugt/konsumiert (`RichTextEditor.tsx`). Wird
 * als Zod-Default in `platformSettings.legal` verwendet: ein frisch
 * aufgesetztes System zeigt sofort einen vollständigen, bearbeitbaren
 * Startzustand statt einer leeren Seite. Ein Platzhalter in eckigen Klammern
 * markiert die eine noch offene Pflichtangabe (PLZ/Ort) — siehe
 * docs/legal.md.
 */
export const DEFAULT_IMPRINT_HTML = `
<h2>Angaben gemäß § 5 DDG</h2>
<p>
  Stefan Meuser<br>
  Leipziger Straße 119<br>
  [PLZ und Ort]
</p>
<h2>Kontakt</h2>
<p>
  Telefon: 0172 8720038<br>
  E-Mail: kontakt@meuser-stefan.de
</p>
<h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
<p>Stefan Meuser, Anschrift wie oben.</p>
<h2>Haftung für Inhalte</h2>
<p>
  Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den
  allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch
  nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach
  Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
</p>
<p>
  Diese Plattform stellt einzelnen Badmintonvereinen eine Vereinsverwaltungssoftware zur
  Verfügung. Inhalte, die ein Verein über sein eigenes, passwortgeschütztes Vereinskonto selbst
  einstellt (z. B. Mitgliederdaten, Termine, Social-Media-Beiträge), stammen von diesem Verein als
  eigenständigem presserechtlich Verantwortlichen für seine Inhalte, nicht vom Betreiber dieser
  Plattform. Bei Bekanntwerden von Rechtsverletzungen werden entsprechende Inhalte umgehend
  entfernt.
</p>
<h2>Haftung für Links</h2>
<p>
  Unser Angebot enthält Links zu externen Webseiten Dritter (z. B. Facebook, Instagram,
  turnier.de), auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden
  Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der
  jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
</p>
<h2>Streitschlichtung</h2>
<p>
  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
  <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr/</a>.
  Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer
  Verbraucherschlichtungsstelle teilzunehmen.
</p>
`.trim();
export const DEFAULT_PRIVACY_HTML = `
<h2>1. Verantwortlicher</h2>
<p>
  Verantwortlicher für den Betrieb dieser Plattform im Sinne der Datenschutz-Grundverordnung
  (DSGVO) ist die im <a href="/impressum">Impressum</a> genannte Person/Stelle
  (kontakt@meuser-stefan.de).
</p>
<p>
  <strong>Wichtiger Hinweis zur Rollenverteilung:</strong> Diese Plattform wird von mehreren
  Badmintonvereinen unabhängig voneinander genutzt (Mandantentrennung). Für die personenbezogenen
  Daten, die ein Verein über seine Mitglieder in seinem eigenen Vereinskonto verarbeitet (z. B.
  Mitgliederlisten, Mannschaftsaufstellungen), ist der jeweilige Verein selbst
  datenschutzrechtlich Verantwortlicher im Sinne von Art. 4 Nr. 7 DSGVO. Der Betreiber dieser
  Plattform verarbeitet diese Daten in dessen Auftrag (Auftragsverarbeitung, Art. 28 DSGVO) und
  trifft die in dieser Erklärung beschriebenen technischen und organisatorischen Maßnahmen zu
  ihrem Schutz.
</p>
<h2>2. Übersicht der Verarbeitungen</h2>
<p>
  Diese Software dient der Verwaltung eines Badmintonvereins: Mitgliederverwaltung,
  Trainingsplanung, Saison-/Mannschaftsspielbetrieb, Vereinstermine, Ausrüstungsverwaltung und
  optional die Veröffentlichung von Beiträgen auf den Social-Media-Kanälen des Vereins. Es findet
  <strong>kein Tracking, keine Analyse-Software und keine Werbung</strong> statt — weder eigene
  Cookies zu diesem Zweck noch Drittanbieter wie Google Analytics oder ein Facebook-Pixel.
</p>
<h2>3. Welche Daten wir verarbeiten</h2>
<h3>3.1 Benutzerkonto (Login)</h3>
<p>
  Bei der Registrierung/Einladung eines Benutzerkontos: E-Mail-Adresse, Anzeigename, Passwort (nur
  als Hash gespeichert, niemals im Klartext), Kontostatus, Zeitpunkt der letzten Anmeldung sowie
  technisch notwendige Bestätigungs-/Rücksetz-Tokens für E-Mail-Verifizierung bzw. „Passwort
  vergessen".
</p>
<h3>3.2 Mitgliedsdaten</h3>
<p>
  Für Vereinsmitglieder (die nicht zwingend selbst ein Benutzerkonto haben müssen, z. B. Kinder):
  Vor- und Nachname, optional E-Mail-Adresse, optional Geschlecht (wird für die Einhaltung der
  Spielordnung bei gemischten Mannschaftsaufstellungen benötigt), optional Geburtsdatum, optional
  eine Notfall-Telefonnummer. Für minderjährige Mitglieder kann zusätzlich ein
  Erziehungsberechtigter mit eigenem Benutzerkonto verknüpft werden, der die Daten des Kindes
  verwalten darf. Es werden bewusst <strong>keine</strong> Bankverbindungen (SEPA), Adressdaten
  oder gesundheitsbezogenen Daten erhoben.
</p>
<h3>3.3 Cookies/Sitzungsdaten</h3>
<p>
  Beim Login wird ein Sitzungs-Cookie gesetzt (technisch zwingend erforderlich, keine Einwilligung
  nötig, Art. 6 Abs. 1 lit. b DSGVO): ein „httpOnly"-Session-Cookie (nicht per JavaScript
  auslesbar) und ein zugehöriges CSRF-Schutz-Cookie, beide standardmäßig 30 Tage gültig. Die
  eigentlichen Sitzungsdaten liegen serverseitig in einem Redis-Zwischenspeicher, nicht im Cookie
  selbst. Nutzt ein Plattform-Administrator die interne „Anzeigen als"-Funktion (Support-Zwecke),
  gilt dafür eine separate, auf eine Stunde befristete Sitzung; jede solche Aktion wird
  protokolliert.
</p>
<h3>3.4 Von einem Verein selbst erstellte Inhalte</h3>
<p>
  Trainings-/Termindaten, Saison- und Spielergebnisse, Ausrüstungslisten, hochgeladene Bilder (z.
  B. Vereinslogo, Spielberichtsfotos, Social-Media-Bilder) sowie Textinhalte für
  Social-Media-Beiträge, die Vereinsmitglieder mit entsprechender Berechtigung selbst eingeben.
</p>
<h2>4. Empfänger und Drittanbieter</h2>
<h3>4.1 E-Mail-Versand</h3>
<p>
  Für automatische E-Mails (z. B. Einladungen, Passwort-Zurücksetzen, Benachrichtigungen) wird je
  nach Vereins- bzw. Plattformkonfiguration ein SMTP-Server oder ein API-basierter
  Mailversand-Dienst genutzt. Dabei werden die E-Mail-Adresse des Empfängers sowie der
  Nachrichteninhalt an diesen Dienst übermittelt.
</p>
<h3>4.2 turnier.de / Deutscher Badminton-Verband (DBV)</h3>
<p>
  Zur Anzeige von Mannschafts- und Spielplandaten ruft die Plattform öffentlich zugängliche Seiten
  von turnier.de ab (rein lesender Zugriff). Es werden dabei <strong>keine personenbezogenen
  Daten unserer Nutzer</strong> an turnier.de übertragen.
</p>
<h3>4.3 Facebook/Instagram (Meta Platforms Ireland Ltd.)</h3>
<p>
  Verbindet ein Vereinsadmin freiwillig die Facebook-Seite oder das Instagram-Konto des Vereins
  (Funktion „Verein → Social Media"), meldet er sich dafür direkt bei Meta an (Facebook-Login).
  Die Plattform erhält dabei von Meta ein Zugriffstoken, das verschlüsselt (AES-256) in unserer
  Datenbank gespeichert wird, sowie das öffentliche Profilbild und den Namen der verbundenen
  Seite/des Kontos. Erstellt ein Social-Media-Beauftragter des Vereins über den Editor einen
  Beitrag und veröffentlicht ihn, werden der Beitragstext und ggf. angehängte Bilder aktiv an die
  Facebook-/Instagram-Graph-API übertragen und dort öffentlich sichtbar. Meta verarbeitet diese
  Daten anschließend als eigener datenschutzrechtlich Verantwortlicher gemäß der
  <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Datenrichtlinie von Meta</a>.
  Ein Verein kann die Verbindung jederzeit selbst über dieselbe Seite trennen.
</p>
<h3>4.4 Datei-Speicherung</h3>
<p>
  Hochgeladene Bilder/Dateien (Logos, Spielberichtsfotos, Social-Media-Bilder) werden je nach
  Serverkonfiguration entweder direkt auf dem Server oder bei einem S3-kompatiblen
  Objektspeicher-Anbieter abgelegt.
</p>
<h2>5. Hosting</h2>
<p>
  Diese Anwendung wird betrieben bei netcup GmbH (Nürnberg, Deutschland). Mit dem
  Hosting-Anbieter besteht, soweit erforderlich, ein Vertrag zur Auftragsverarbeitung nach Art. 28
  DSGVO.
</p>
<h2>6. Speicherdauer und Löschung</h2>
<p>
  Mitglieds- und Kontodaten werden gespeichert, solange die Mitgliedschaft bzw. das Benutzerkonto
  besteht. Ein Verein kann in seinen Einstellungen eine Inaktivitätsschwelle konfigurieren: Konten,
  die seit der letzten Anmeldung länger als diese Schwelle inaktiv sind, werden automatisch
  inklusive ihrer Mitgliedsdaten gelöscht — ausgenommen Plattform-Administratoren und der jeweils
  letzte verbleibende Vereinsadministrator eines Vereins (damit ein Verein nie unbeabsichtigt
  handlungsunfähig wird). Unabhängig davon kann jeder Verein Mitgliedsdaten jederzeit manuell
  löschen.
</p>
<h2>7. Ihre Rechte</h2>
<p>Ihnen stehen nach der DSGVO folgende Rechte zu:</p>
<ul>
  <li>Auskunft über die zu Ihrer Person gespeicherten Daten (Art. 15 DSGVO)</li>
  <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
  <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
  <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
  <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
  <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
  <li>Beschwerde bei einer Datenschutz-Aufsichtsbehörde (Art. 77 DSGVO)</li>
</ul>
<p>
  Da die Mitgliedsdaten in der Regel von Ihrem Verein verwaltet werden (siehe Abschnitt 1), wenden
  Sie sich für die Ausübung dieser Rechte zunächst an Ihren Verein. Für Fragen zum technischen
  Betrieb der Plattform erreichen Sie uns unter kontakt@meuser-stefan.de.
</p>
<h2>8. Datensicherheit</h2>
<p>
  Jeder Verein sieht ausschließlich seine eigenen Daten — technisch erzwungen durch
  Datenbank-seitige Zugriffsregeln (Row-Level-Security), nicht nur durch die Anwendung selbst.
  Passwörter werden ausschließlich als Hash gespeichert. Zugriffstoken für verbundene
  Social-Media-Konten werden verschlüsselt abgelegt und niemals an das Frontend ausgeliefert. Die
  Verbindung zu dieser Seite ist per HTTPS verschlüsselt.
</p>
<h2>9. Änderungen dieser Datenschutzerklärung</h2>
<p>
  Wir passen diese Datenschutzerklärung an, sobald sich die Datenverarbeitung durch neue Funktionen
  oder Vorgaben ändert. Es gilt die jeweils bei einem Besuch dieser Seite aktuelle Fassung.
</p>
`.trim();
//# sourceMappingURL=legal.js.map